import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  MdDashboard,
  MdCalendarMonth,
  MdBarChart,
  MdExtension,
  MdLogout,
  MdArrowBack,
} from 'react-icons/md';
import { PiMegaphoneFill } from 'react-icons/pi';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import { useDispatch } from 'react-redux';
import { getLogoutRequest, getLoginRequestReset } from '../../redux/slices/authSlice';
import ENDPOINTS from '../../api/endPoints';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  route: string;
}

const Navbar: React.FC = () => {
  const { navigate } = useRestaurantNav();
  const location = useLocation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const dispatch = useDispatch();

  const currentRestaurantId = restaurantId || localStorage.getItem('restaurantId');

  const navItems: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <MdDashboard size={24} />,
      route: `/${currentRestaurantId}/dashboard`,
    },
    {
      id: 'calendar',
      label: 'Calendar',
      icon: <MdCalendarMonth size={24} />,
      route: `/${currentRestaurantId}/marketing-calendar`,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <MdBarChart size={24} />,
      route: `/${currentRestaurantId}/analytics`,
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: <MdExtension size={24} />,
      route: `/${currentRestaurantId}/integrations`,
    },
  ];

  const handleNavigation = (route: string) => {
    navigate(route);
  };

  const handleLogout = () => {
    dispatch(getLogoutRequest());
    dispatch(getLoginRequestReset());
    localStorage.clear();
    window.location.href = '/login';
  };

  const handleBackToVoiceAI = () => {
    window.location.href = ENDPOINTS.voiceAI;
  };

  const isActive = (route: string) => location.pathname === route;

  return (
    <>
      <nav className="hidden md:flex md:flex-col md:w-[90px] md:h-screen md:bg-[#1a1a1a] md:border-r md:border-gray-800">
        {/* Logo Section */}
        <div className="flex items-center justify-center py-4 border-b border-gray-800">
          <button
            onClick={() => handleNavigation(`/${currentRestaurantId}/dashboard`)}
            className="cursor-pointer hover:opacity-80 transition-opacity"
          >
            <img src="/assets/images/promoflowai-logo-sm.png"
                alt="PromoFlow AI"
                className="w-auto h-[65px]" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 flex flex-col items-center py-6 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.route)}
              className={`w-[80px] cursor-pointer flex flex-col items-center justify-center py-4 rounded-xl transition-all duration-200 ${
                isActive(item.route)
                  ? ' border- hover:bg-[#2a2a2a] text-brand-primary'
                  : 'text-gray-300 hover:bg-[#2a2a2a] hover:text-gray-200'
              }`}
            >
              <div className="mb-2">{item.icon}</div>
              <span className="text-[10px] font-normal">{item.label}</span>
            </button>
          ))}
        </div>

        {/* User Profile Section */}
        <div className="border-t border-gray-800 p-4 flex flex-col items-center justify-center space-y-2">
          {/* Back to Voice AI Button */}
          <button
            onClick={handleBackToVoiceAI}
            className="bg-brand-primary text-white cursor-pointer hover:opacity-90 transition-all p-2 rounded-full"
            title="Back to Voice AI"
          >
            <MdArrowBack size={24} />
          </button>
        </div>
      </nav>

      {/* Mobile Top Navbar - Sticky */}
      <nav className="md:hidden sticky top-0 z-50 bg-[#1a1a1a] border-b border-gray-800">
        {/* Logo and User Section */}
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => handleNavigation(`/${currentRestaurantId}/dashboard`)}
            className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
          >
            <PiMegaphoneFill size={24} className="text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-600 rounded flex items-center justify-center">
              <span className="text-white text-xs font-semibold">JD</span>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-400  hover:text-orange-500 transition-colors p-2"
              title="Logout"
            >
              <MdLogout size={20} />
            </button>
          </div>
        </div>

        {/* Navigation Items - Horizontal Scroll */}
        <div className="flex overflow-x-auto px-4 pb-3 scrollbar-hide">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.route)}
              className={`flex-shrink-0 flex flex-col items-center justify-center px-4 py-2 rounded-xl transition-all duration-200 min-w-[80px] ${
                isActive(item.route)
                  ? 'bg-[#2a2a2a] text-orange-500'
                  : 'text-gray-400 hover:bg-[#2a2a2a] hover:text-gray-200'
              }`}
            >
              <div className="mb-1 text-[20px]">{item.icon}</div>
              <span className="text-xs font-medium whitespace-nowrap">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
